<script setup lang="ts">
import { keyfeaturesIcons } from 'assets/js/keyfeaturesIcons'
import type {
  HouseKeyFeatures,
  HouseDetailsAttributes,
  HouseAreaEquipment,
  Locale,
} from 'lc-services/types'

const { trackEvent } = useTracking()
const { isDesktopOrTablet } = useBreakpoint()

const props = defineProps<{
  areaEquipments: HouseAreaEquipment[]
  house: HouseDetailsAttributes
  featuredEquipments: HouseKeyFeatures
}>()

const showModalDesktop = ref(false)
const showModalMobile = ref(false)

const numberKeyFeatures = computed(() => (isDesktopOrTablet.value ? 8 : 6))

const toggleModal = () => {
  if (isDesktopOrTablet.value) {
    showModalDesktop.value = !showModalDesktop.value
  } else {
    showModalMobile.value = !showModalMobile.value
  }

  if (showModalMobile.value || showModalDesktop.value) {
    trackEvent({
      category: 'Product page',
      event: 'rooms_and_equipments_viewed',
      house_id: Number(props.house.id),
    })
  }
}

const getIconName = (identifier: string) =>
  keyfeaturesIcons.find((k) => k.identifier === identifier)?.icon || 'sparkles'
</script>

<template>
  <div
    v-if="featuredEquipments.featured?.length"
    data-testid="house-keyfeatures"
  >
    <h2 class="m-0 mb-5 text-2xl md:mb-6">
      {{ $t('house.keyfeatures.title') }}
    </h2>

    <div
      v-if="house.iconicCollection || house.exclusivity"
      class="mb-5 flex md:mb-6"
    >
      <span
        v-if="house.iconicCollection"
        class="mr-2 rounded bg-success/20 px-2 py-1 text-xs text-gray-700"
      >
        {{ $t('global.iconic') }}
      </span>
      <span
        v-if="house.exclusivity"
        class="rounded bg-primary-100 px-2 py-1 text-xs text-gray-700"
      >
        {{ $t('global.exclusive') }}
      </span>
    </div>

    <div class="flex flex-wrap items-center md:mb-7">
      <template v-for="(keyfeature, index) in featuredEquipments.featured">
        <div
          v-if="index < numberKeyFeatures"
          :key="index"
          class="mb-4 flex w-full items-center md:w-1/2"
        >
          <BaseIcon
            class="mr-2 shrink-0"
            :name="getIconName(keyfeature.identifier)"
            :size="2"
          />
          <span>
            {{ keyfeature.name[$i18n.locale as Locale] }}
          </span>
        </div>
      </template>
    </div>

    <BaseButton
      class="w-full !text-md !font-bold md:w-auto lg:w-[275px]"
      color="secondary"
      data-cy="see-all-equipments"
      variant="outline"
      @click="toggleModal"
    >
      {{ $t('house.seeAllEquipments', featuredEquipments.equipments_count) }}
    </BaseButton>

    <BaseModalV2
      v-model="showModalDesktop"
      size="m"
      min-height
      @close-modal="toggleModal"
    >
      <template #content>
        <h2 class="mb-10 mt-1 text-center text-4xl">
          {{ $t('house.all_equipments') }}
        </h2>
        <HouseRoomsAndEquipmentsModal :area-equipments="areaEquipments" />
      </template>
    </BaseModalV2>

    <div
      v-if="showModalMobile"
      class="fixed left-0 top-0 z-[9999] h-screen w-full overflow-y-auto bg-white p-4"
    >
      <BaseButton
        class="mb-6 !flex items-center"
        color="black"
        variant="link"
        @click="toggleModal"
      >
        <BaseIcon class="mr-3.5" name="navArrowLeft" />
        <span class="text-md font-medium leading-none">
          {{ $t('account.back') }}
        </span>
      </BaseButton>
      <HouseRoomsAndEquipmentsModal :area-equipments="areaEquipments" />
    </div>
  </div>
</template>
